import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@Redux/detail/store';
import { I_FlightList } from '@Redux/detail/Group/interfaces';
import styles from '../style.module.scss';
import cN from 'classnames';
import { AirPlane, Document, Info, Time } from '@components/Common/Icons';
import { Link } from 'react-scroll';
import dayjs from 'dayjs'
import GroupTripInfoFlight from '@components/Common/GroupTripInfoFlight';

interface I_RoundInfo {
    roundTitle: string;
    roundList: I_FlightList[]
}

const RoundInfo: React.FC<I_RoundInfo> = ({roundTitle, roundList}) => {
    const displayedAirlines: Set<string> = new Set(); // check轉機航空公司是否重複

    roundList.forEach((item)=>{
        if (!displayedAirlines.has(item?.Airline)) {
            displayedAirlines.add(item?.Airline);
        }
    })

    function calculateDayDifference(first: string, last: string) {
        const dateStart = dayjs(first)
        const dateEnd = dayjs(last)
        if(dateEnd.diff(dateStart, 'day') > 0) return `+${dateEnd.diff(dateStart, 'day')}`
    }

    // 航段總飛行時間
    function calculateFlyingTime(flightArray: I_FlightList[] | undefined) {
        let totalFlightTime = dayjs.duration(0);
        if(flightArray) {
            totalFlightTime = flightArray?.reduce((sum, item) => {
                const [hour, minute] = item.FlightTime.split(":").map(Number);
                return sum.add({ hours: hour, minutes: minute });
            }, totalFlightTime);
        const totalHours = Math.floor(totalFlightTime.asHours());
            return `${totalHours ? `${totalHours}h` : ''}${totalFlightTime.get('minutes')}m`;
        }
    }

    return (
        <div className={styles.infos}>
            <div className={styles['sub-title-wrap']}>
                <div className={styles['sub-title']}>{roundTitle}</div>
                <div className={styles['flight-wrap']}>
                    <div>{roundList?.[0].DepartureDate}</div>
                    {roundList && <div className={styles['airlines']}>
                        {Array.from(displayedAirlines).map((item, index) => {
                            return (<span key={`Airline-${index}`}>
                                {index > 0 && <span className={cN(styles['plus'])}>+</span>}
                                <span className={cN(styles['airline'])}>{item}</span>
                            </span>)
                        })}
                    </div>}
                </div>
            </div>
            <ul>
                <li className={styles.dep}>
                    <div className={cN(styles['circle'], styles['time'])}>{roundList?.[0]?.DepartTime}</div>
                    <div className={cN(styles['airport-wrap'])}>
                        <span className={cN(styles['mr-5'])}>{roundList?.[0]?.DepartureAirportID}</span>
                        <span className={cN(styles['airport-name'])}>{roundList?.[0]?.DepartureAirport}</span>
                    </div>
                </li>
                <li className={cN(styles['flex'])}>
                    <div className={styles.decoration} />
                    <div className={cN(styles['time'], styles['duration'])}>
                        <span>{calculateFlyingTime(roundList)}</span>
                    </div>
                    {roundList && roundList.length > 1 && <div className={cN(styles['transfer-count'])}>轉機 {roundList.length - 1} 次</div>}
                </li>
                <li className={styles.arr}>
                    <div className={cN(styles['circle'], styles['time'])}>
                        {roundList?.[roundList.length - 1]?.ArriveTime}
                        {roundList && <span className={cN(styles['next-day'])}>
                            {calculateDayDifference(roundList?.[0]?.DepartureDate, roundList?.[roundList.length - 1]?.ArriveDate)}
                        </span>}
                    </div>
                    <div className={cN(styles['airport-wrap'])}>
                        <span className={cN(styles['mr-5'])}>{roundList?.[roundList.length - 1]?.ArriveAirportID}</span>
                        <span className={cN(styles['airport-name'])}>{roundList?.[roundList.length - 1]?.ArriveAirport}</span>
                    </div>
                </li>
            </ul>
        </div>
    )

}

const Flight: React.FC = () => {
    const Group = useSelector((store: RootState) => store.groupReducer);    
    const {LeaveTeamReg} = useSelector((store: RootState) => store.itinerariesReducer);
    const [flightInfoOpen, setFlightInfoOpen] = useState<boolean>(false);
    const directionList = Group.FlightInfo?.GroupList[0]?.DirectionList;
    const outboundList = directionList?.find((i) => i.Name === '去程')?.FlightList
    const inboundList = directionList?.find((i) => i.Name === '回程')?.FlightList

    if (!directionList || !outboundList || !inboundList) {
        return null;
    }    

    return outboundList.length > 0 || inboundList.length > 0 ? (
        <div className={cN(styles['group-trip-info'], styles.flight, 'group_trip_info', 'flight')}>
            <div className={cN(styles['title-wrap'])}>
                <div className={cN(styles.title)}>參考航班</div>
                <div className={cN(styles["tips"], styles["pc"])}>
                    <span className={cN(styles['icon-wrap'])}><Info /></span><span className={cN(styles['text'])}>航班時間僅為參考，最終確定之使用航班，以說明會資料為準！</span>
                </div>
                {LeaveTeamReg && LeaveTeamReg.length > 0 && <div className={cN(styles.rules)}>
                    <Link to="leaveTeam" smooth={true} offset={-140}>
                        <Document /> <span>查看團體航班規定</span>
                    </Link>
                </div>}
            </div>
            <div className={styles.wrap}>
                <div className={styles.content}>
                    <RoundInfo roundTitle='去程' roundList={outboundList!}/>
                    <RoundInfo roundTitle='回程' roundList={inboundList!}/>
                    <div className={cN(styles['tips'], styles['mobile'])}>
                        <span className={cN(styles['icon-wrap'])}><Info /></span><span>航班時間僅為參考，最終確定之使用航班，以說明會資料為準！</span>
                    </div>
                </div>
                <span className={cN(styles['btns'])} onClick={() => setFlightInfoOpen(!flightInfoOpen)}>
                    <AirPlane fill="#222" />
                    航班詳情
                </span>
            </div> 

            <GroupTripInfoFlight
                directionList={directionList}
                open={flightInfoOpen}
                ToggleLightBox={() => setFlightInfoOpen(false)}
            />
        </div>
    ) : null;
};

export default Flight;
